// js/shared/public-home.js — the home page's behaviour. QUEUE ITEM KV.
//
// Five things, and the page needs none of them to be READ: with JavaScript off
// every section is there, the menu button does nothing, the language links go
// to `/?lang=es`, the form is a form and the phone bar never shows.
//
//   1. THE LANGUAGE (queue items BS 12, BQ / R61): `?lang=` first, then what a
//      public page chose last time on this browser, then the browser. The
//      "Español" link switches in place and keeps the page where it is.
//   2. THE PHONE MENU (E-01, E-05): a labelled 44px "Menu" button with
//      aria-expanded and "Close menu" while open; the menu is the kit's bottom
//      sheet (a modal dialog: focus stays inside, Escape, the ✕ and the scrim
//      close it, and focus comes back to the button).
//   3. THE CONTACT FORM (E-04, E-12; JW's always-send stays): a phone number
//      with "Use email instead", an error AT each field with aria-invalid and
//      focus on the first, "Sending…" on the disabled button, the form
//      replaced by "Got it, <first name>." only after the send answered 2xx,
//      and a failure that keeps the answers and gives the number.
//   4. THE PHONE CALL BAR (Sep 30): one IntersectionObserver on the hero's
//      buttons and the form; hidden while either is on screen and while the
//      menu is open, so a screen never shows two filled buttons.
//   5. THE RISE-IN (Oct 1): groups BELOW the first screen at load rise 12px
//      and fade in, 300ms, 60ms apart, once. Never under Reduce motion, never
//      a group that was on the first screen, and never without this script
//      (a group waits only after this script has marked it).
//
// PURE HALF / DOM HALF, as js/shared/public-site.js: the date words and the
// form's checks take plain data, so tests/unit/shared/kv-public-home.test.mjs
// runs them in node.

import { applyMarketingLang, marketingLangFrom, marketingPack } from './marketing-i18n.js';
// Queue item KVb: the field error and the first name are shared with the
// /for-trainers waitlist (js/shared/public-field.js).
import { firstName, setFieldError } from './public-field.js';

const LANG_KEY = 'kinetiqaf_public_lang';
const SUPABASE_URL = 'https://rvrvfdjmhwymkbmrzoza.supabase.co';
const SUPABASE_ANON_KEY = 'sb_publishable_fs3ThPGEwQujZTcUVux8Tw_dLe3Bknl';

// ── the pure half ────────────────────────────────────────────────────────

/** The next business day after `from` (Monday to Friday), as a local Date. */
export function nextBusinessDay(from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 12);
  do { d.setDate(d.getDate() + 1); } while (d.getDay() === 0 || d.getDay() === 6);
  return d;
}

/** "Thursday, February 19" / "jueves 19 de febrero". */
export function dayWords(date, lang) {
  const es = String(lang || '').startsWith('es');
  return date.toLocaleDateString(es ? 'es-MX' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

/** The first word of a name, for "Got it, Grace." (js/shared/public-field.js). */
export { firstName };

/**
 * The contact form's checks. `mode` is 'phone' or 'email' (which of the two
 * the reader is giving). → { name?, phone?, email?, service? }, each a pack
 * key naming the message; an empty object means the form may send.
 * anon_insert_leads applies the same rule in the database: a phone of at least
 * seven digits, or a well-formed email.
 */
export function contactErrors({ name, phone, email, mode, service }) {
  const out = {};
  if (!String(name || '').trim()) out.name = 'form.errName';
  if (mode === 'email') {
    if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(String(email || '').trim())) out.email = 'form.errEmail';
  } else if (String(phone || '').replace(/\D/g, '').length < 7) {
    out.phone = 'form.errPhone';
  }
  if (!service) out.service = 'form.errService';
  return out;
}

// ── the DOM half ─────────────────────────────────────────────────────────

function readLang() {
  try {
    return marketingLangFrom({ search: location.search, stored: localStorage.getItem(LANG_KEY), navigatorLang: navigator.language });
  } catch (_) {
    // Storage blocked: the URL alone still decides.
    return /[?&]lang=es/i.test(location.search) ? 'es' : 'en';
  }
}

let LANG = 'en';
const T = (key) => marketingPack(LANG)[key] || '';

function paint() {
  applyMarketingLang(document, LANG);
  // The switch names the OTHER language, in that language.
  const other = LANG === 'es' ? 'en' : 'es';
  document.querySelectorAll('[data-lang-switch]').forEach((a) => {
    a.setAttribute('href', '/?lang=' + other);
    a.setAttribute('hreflang', other);
    a.querySelectorAll('[lang]').forEach((el) => el.setAttribute('lang', other));
  });
  syncMenuLabel();
  syncSwap();
}

function wireLanguage() {
  document.querySelectorAll('[data-lang-switch]').forEach((a) => a.addEventListener('click', (e) => {
    e.preventDefault();
    LANG = LANG === 'es' ? 'en' : 'es';
    try { localStorage.setItem(LANG_KEY, LANG); } catch (_) { /* storage blocked: the URL still carries it */ }
    try {
      const url = new URL(location.href);
      url.searchParams.set('lang', LANG);
      history.replaceState(history.state, '', url.toString());
    } catch (_) { /* an older browser: the repaint below still happens */ }
    paint();
    if (typeof window.kafPaintPublicSite === 'function') window.kafPaintPublicSite();
    closeMenu(false);
  }));
}

// 2 — the phone menu.
const menuBtn = () => document.getElementById('menu-btn');
const drawer = () => document.getElementById('site-menu');
let barSync = () => {};

function syncMenuLabel() {
  const btn = menuBtn();
  if (!btn) return;
  const open = btn.getAttribute('aria-expanded') === 'true';
  const label = btn.querySelector('[data-menu-label]');
  if (label) label.textContent = T(open ? 'nav.closeMenu' : 'nav.menu') || (open ? 'Close menu' : 'Menu');
}

const scrim = () => document.getElementById('site-menu-scrim');

function openMenu() {
  const btn = menuBtn(); const d = drawer();
  if (!btn || !d) return;
  d.hidden = false;
  if (scrim()) scrim().hidden = false;
  document.documentElement.style.overflow = 'hidden';
  btn.setAttribute('aria-expanded', 'true');
  syncMenuLabel();
  barSync();
  const first = d.querySelector('a, button');
  if (first) first.focus();
}

function closeMenu(returnFocus = true) {
  const btn = menuBtn(); const d = drawer();
  if (!btn || !d || d.hidden) return;
  d.hidden = true;
  if (scrim()) scrim().hidden = true;
  document.documentElement.style.overflow = '';
  btn.setAttribute('aria-expanded', 'false');
  syncMenuLabel();
  barSync();
  if (returnFocus) btn.focus();
}

function wireMenu() {
  const btn = menuBtn(); const d = drawer();
  if (!btn || !d) return;
  btn.addEventListener('click', () => (d.hidden ? openMenu() : closeMenu()));
  const close = document.getElementById('menu-close');
  if (close) close.addEventListener('click', () => closeMenu());
  if (scrim()) scrim().addEventListener('click', () => closeMenu());
  document.addEventListener('keydown', (e) => {
    if (d.hidden) return;
    if (e.key === 'Escape') { closeMenu(); return; }
    // A modal sheet keeps the keyboard inside it (WCAG 2.4.3).
    if (e.key === 'Tab') {
      const stops = [...d.querySelectorAll('a[href], button:not([disabled])')];
      const first = stops[0]; const last = stops[stops.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });
  // A row that jumps within the page closes the menu on its way.
  d.querySelectorAll('a[href^="#"]').forEach((a) => a.addEventListener('click', () => closeMenu(false)));
  // Widening past the phone layout with the menu open puts the nav back.
  if (window.matchMedia) {
    const wide = window.matchMedia('(min-width: 1080px)');
    const onWide = () => { if (wide.matches) closeMenu(false); };
    if (wide.addEventListener) wide.addEventListener('change', onWide);
  }
}

// 3 — the contact form.
let mode = 'phone';

function syncSwap() {
  const swap = document.getElementById('f-swap');
  if (swap) swap.textContent = T(mode === 'phone' ? 'form.useEmail' : 'form.usePhone');
}

function field(name) { return document.querySelector(`[data-field="${name}"]`); }

function setError(name, key) {
  setFieldError(field(name), key ? T(key) : null);
}

function wireForm() {
  const form = document.getElementById('contact-form');
  if (!form) return;
  const swap = document.getElementById('f-swap');
  swap.addEventListener('click', () => {
    mode = mode === 'phone' ? 'email' : 'phone';
    field('phone').hidden = mode !== 'phone';
    field('email').hidden = mode !== 'email';
    setError('phone', null); setError('email', null);
    syncSwap();
    document.getElementById(mode === 'phone' ? 'f-phone' : 'f-email').focus();
  });
  // A path card's "Talk to me about this" arrives with its path chosen (GV).
  document.querySelectorAll('[data-path]').forEach((a) => a.addEventListener('click', () => {
    document.getElementById('f-service').value = a.getAttribute('data-path');
    setError('service', null);
  }));
  form.addEventListener('submit', (e) => { e.preventDefault(); send(); });
}

function send() {
  const name = document.getElementById('f-name').value.trim();
  const phone = mode === 'phone' ? document.getElementById('f-phone').value.trim() : '';
  const email = mode === 'email' ? document.getElementById('f-email').value.trim() : '';
  const service = document.getElementById('f-service').value;
  const trap = document.getElementById('f-website');
  const errors = contactErrors({ name, phone, email, mode, service });
  ['name', 'phone', 'email', 'service'].forEach((k) => setError(k, errors[k] || null));
  const order = ['name', mode, 'service'].filter((k) => errors[k]);
  if (order.length) {
    field(order[0]).querySelector('input, select').focus();
    return;
  }
  const btn = document.getElementById('form-btn');
  const fail = document.getElementById('form-fail');
  fail.hidden = true;
  btn.disabled = true;
  btn.textContent = T('form.sending');
  const reach = email || phone;
  const first = firstName(name);

  // Queue item JW (E-03): the form always sends; the honeypot travels with it
  // and the SERVER refuses it (anon_insert_leads, and submit-form for the
  // email). BP item 3: no `source` or `status` here (no anon grant on either).
  // CI-META-1d reads this body's keys against anon's INSERT grants on leads.
  const leadPayload = {
    name,
    email: email || null,
    phone: phone || null,
    service,
    message: null,
    website: (trap && trap.value) || '',
    utm_source: (typeof window.kafCampaignCode === 'function') ? window.kafCampaignCode() : null,
  };
  const leadSent = fetch(SUPABASE_URL + '/rest/v1/leads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY, Prefer: 'return=minimal' },
    body: JSON.stringify(leadPayload),
  }).then((res) => { if (!res.ok) console.warn('[contact] lead row refused', res.status); })
    .catch((err) => console.warn('[contact] lead row not sent', err && err.message));

  const failed = () => {
    btn.disabled = false;
    btn.textContent = T('form.retry');
    fail.hidden = false;
  };
  // The email is what reaches James, so it is what "Got it" waits for.
  fetch('/.netlify/functions/submit-form', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      subject: 'KinetiqAF free call request: ' + name,
      from_name: name, replyto: email || undefined, name, email,
      phone: phone || 'Not provided', service,
      message: 'Asked for a free 20-minute call. Call back on: ' + reach,
      website: (trap && trap.value) || '',
    }),
  })
    .then((res) => (res.ok ? res.json() : { success: false, status: res.status }))
    .then(async (res) => {
      await leadSent;
      if (!res || !res.success) { console.warn('[contact] email refused', res && res.status); failed(); return; }
      const day = dayWords(nextBusinessDay(new Date()), LANG);
      document.getElementById('form-sent-title').textContent = T('form.gotIt').replace('{name}', first);
      document.getElementById('form-sent-when').textContent = T(email ? 'form.writeBy' : 'form.callBy').replace('{day}', day);
      document.getElementById('contact-form').hidden = true;
      const sent = document.getElementById('form-sent');
      sent.hidden = false;
      sent.focus();
    })
    .catch((err) => { console.warn('[contact] email not sent', err && err.message); failed(); });
}

// 4 — the phone call bar.
function wireBar() {
  const bar = document.getElementById('call-bar');
  const cta = document.getElementById('hero-cta');
  const box = document.getElementById('contact-box');
  if (!bar || !cta || !box || !('IntersectionObserver' in window)) return;
  let ctaOn = true; let formOn = false;
  bar.hidden = false;
  bar.classList.add('live');
  document.body.classList.add('has-bar');
  barSync = () => {
    const d = drawer();
    bar.classList.toggle('off', ctaOn || formOn || !!(d && !d.hidden));
  };
  const watch = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.target === cta) ctaOn = e.isIntersecting; else formOn = e.isIntersecting; });
    barSync();
  });
  watch.observe(cta);
  watch.observe(box);
}

// 5 — the rise-in.
function wireRise() {
  const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  if (reduce || !('IntersectionObserver' in window)) return;
  // "Below the first screen" is decided once the web fonts have arrived: the
  // headline's fallback face is a different height, and a group judged below
  // the fold in it could end up on the first screen when Archivo lands.
  const ready = (document.fonts && document.fonts.ready) ? document.fonts.ready : Promise.resolve();
  ready.then(markBelow, markBelow);
}

function markBelow() {
  if (document.documentElement.classList.contains('motion')) return;
  const below = [...document.querySelectorAll('.rise')].filter((g) => g.getBoundingClientRect().top > window.innerHeight);
  if (!below.length) return;
  document.documentElement.classList.add('motion');
  const rise = new IntersectionObserver((entries) => {
    // A group that is on screen rises; one the reader has already scrolled
    // past (a fast fling, an anchor jump) is simply shown, so nothing above
    // the screen is ever left waiting.
    entries.forEach((e) => {
      if (e.isIntersecting || e.boundingClientRect.top < 0) { e.target.classList.remove('wait'); rise.unobserve(e.target); }
    });
  }, { threshold: 0.1 });
  below.forEach((g) => { g.classList.add('wait'); rise.observe(g); });
}

// The questions in two columns on a computer, alternating (1, 3, 5, 7 left;
// 2, 4, 6, 8 right), so the two drawn open land one in each. One column, in
// order, on a phone and without this script.
function wireFaq() {
  const faq = document.querySelector('[data-faq]');
  if (!faq || !window.matchMedia) return;
  const items = [...faq.querySelectorAll('details')];
  const one = faq.querySelector('[data-faq-col]');
  const two = one.cloneNode(false);
  const wide = window.matchMedia('(min-width: 761px)');
  const lay = () => {
    if (wide.matches) {
      items.forEach((d, i) => (i % 2 === 0 ? one : two).appendChild(d));
      if (!two.parentNode) faq.appendChild(two);
      faq.classList.add('cols');
    } else {
      items.forEach((d) => one.appendChild(d));
      two.remove();
      faq.classList.remove('cols');
    }
  };
  lay();
  if (wide.addEventListener) wide.addEventListener('change', lay);
}

function boot() {
  LANG = readLang();
  paint();
  wireLanguage();
  wireMenu();
  wireForm();
  wireFaq();
  wireBar();
  wireRise();
}

// A unit run that fakes `window`/`document` imports this file for its pure half; with no
// `location` it is not a page, and booting there threw and killed the whole run.
if (typeof window !== 'undefined' && typeof document !== 'undefined' && typeof location !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
